"use client";

import { formatRelative } from "date-fns";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import type React from "react";
import { useMemo, useState } from "react";
import { mapCartRecordToCart, useCart } from "~/hooks/cart";
import { useDealsByDispatchOption, useLocationDeals } from "~/hooks/deals";
import { useCartMenu, useMenu } from "~/hooks/menu-v2";
import {
  useAllowOrderNotes,
  useAvailableOrderDispatchOptions,
  useOrderNotesMaxLength,
} from "~/hooks/ordering";
import { useRecentCarts } from "~/hooks/recent-cart";
import { useUser } from "~/hooks/user";
import {
  AccountCircleOutlinedIcon,
  BoltIcon,
  CartIcon,
  LocalActivityOutlinedIcon,
  RedeemOutlinedIcon,
  RoomServiceOutlinedIcon,
} from "~/icons";
import type { Cart, CartRecord, Page } from "~/types";
import { mapCartDetails } from "~/utils/cart";
import { Box } from "../Box/Box";
import { Button } from "../Button/Button";
import { Drawer } from "../Drawer/Drawer";
import { OptionButton } from "../OptionButton/OptionButton";
import { Stack } from "../Stack/Stack";
import { Text } from "../Text/Text";
import { PromoCodeDrawer } from "../VoucherCode/PromoCodeDrawer";
import styles from "./BottomNavBar.module.scss";

type NavBarItem = "MENU" | "LOYALTY" | "CODE" | "ACCOUNT";

const BOTTOM_NAV_Z_INDEX = 3;
const HIDDEN_SECTIONS = ["cart", "when", "delivery-address"];
const JETMAN_WIDTH_PIXELS = 120;
const JETMAN_HEIGHT_PIXELS = 108;

export function BottomNavBarConditionalWrapper(props: {
  locationRef: string;
  activeItem: Page | undefined;
}): React.ReactNode {
  const pathname = usePathname();
  const section = pathname.split("/")[2];

  if (!section || HIDDEN_SECTIONS.includes(section)) return null;

  return (
    <>
      <Box className={styles.navBarSpacer} />
      <BottomNavBarCart
        locationRef={props.locationRef}
        activeItem={props.activeItem}
      />
    </>
  );
}

export function BottomNavBarCart(props: {
  locationRef: string;
  activeItem: Page | undefined;
}): React.ReactNode {
  const { locationRef } = props;

  const { cart, dispatchOption, timeSlot } = useCart(locationRef);
  const { user, isLoading: userIsLoading, isGuest } = useUser();

  const { availableOrderDispatchOptions } =
    useAvailableOrderDispatchOptions(locationRef);

  const { carts: recentCarts, loading: loadingRecentCarts } = useRecentCarts(
    locationRef,
    3,
  );
  const { menu, isLoading: loadingMenu } = useMenu(dispatchOption, timeSlot);
  const { getDeals, loading: loadingDeals } =
    useDealsByDispatchOption(locationRef);

  const availableRecentCarts = useMemo(
    () =>
      recentCarts.filter((recentCart) => {
        const { cartDetails, hasUnavailableItems } = mapCartDetails(
          mapCartRecordToCart(recentCart).items,
          menu,
          getDeals(recentCart.fulfillmentType || "PICK_UP"),
        );

        return cartDetails.length > 0 && !hasUnavailableItems;
      }),
    [recentCarts, menu, getDeals],
  );

  const [reOrderDrawerOpen, setReOrderDrawerOpen] = useState(false);
  const [codeDrawerOpen, setCodeDrawerOpen] = useState(false);

  return (
    <>
      <BottomNavBar
        locationRef={locationRef}
        activeItem={props.activeItem}
        displayMenu={Boolean(availableOrderDispatchOptions.length)}
        authenticated={Boolean(user && !isGuest)}
        userIsLoading={userIsLoading}
        cart={cart}
        recentCarts={availableRecentCarts}
        reOrderLoading={loadingMenu || loadingDeals || loadingRecentCarts}
        reOrderDrawerOpen={reOrderDrawerOpen}
        setReOrderDrawerOpen={setReOrderDrawerOpen}
        codeDrawerOpen={codeDrawerOpen}
        setCodeDrawerOpen={setCodeDrawerOpen}
      />
      <PromoCodeDrawer
        locationRef={locationRef}
        open={codeDrawerOpen}
        setOpen={setCodeDrawerOpen}
        captureInitialCode={props.activeItem !== "promo-code"}
      />
    </>
  );
}

export function BottomNavBar(props: {
  locationRef: string;
  activeItem: Page | undefined;
  displayMenu: boolean;
  authenticated: boolean;
  userIsLoading: boolean;
  cart: Cart;
  recentCarts: CartRecord[];
  reOrderLoading: boolean;
  reOrderDrawerOpen: boolean;
  setReOrderDrawerOpen: (open: boolean) => void;
  codeDrawerOpen: boolean;
  setCodeDrawerOpen: (open: boolean) => void;
}): React.ReactNode {
  const { locationRef } = props;
  const activeItem = getActiveItem(props.activeItem);

  const accountHref = props.authenticated
    ? `/${locationRef}/account`
    : `/login?next=${encodeURIComponent(`/${locationRef}/account`)}`;

  return (
    <>
      <Box
        width="full"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          zIndex: BOTTOM_NAV_Z_INDEX,
        }}
      >
        <div className={styles.navBar}>
          <div className={styles.navBarItems}>
            <NavItem
              name="MENU"
              href={props.displayMenu ? `/${locationRef}/menu` : undefined}
              active={activeItem === "MENU"}
            >
              <RoomServiceOutlinedIcon />
            </NavItem>
            <NavItem
              name="LOYALTY"
              href={`/${locationRef}/loyalty`}
              active={activeItem === "LOYALTY"}
            >
              <RedeemOutlinedIcon />
            </NavItem>
            <CentreNavItem
              locationRef={locationRef}
              cart={props.cart}
              recentCarts={props.recentCarts}
              reOrderLoading={props.reOrderLoading}
              setReOrderDrawerOpen={props.setReOrderDrawerOpen}
            />
            <NavItem
              name="CODE"
              onClick={() => {
                props.setCodeDrawerOpen(true);
              }}
              active={props.codeDrawerOpen || activeItem === "CODE"}
            >
              <LocalActivityOutlinedIcon />
            </NavItem>
            <NavItem
              name={"ACCOUNT"}
              href={props.userIsLoading ? undefined : accountHref}
              active={activeItem === "ACCOUNT"}
            >
              <AccountCircleOutlinedIcon />
            </NavItem>
          </div>
        </div>
      </Box>
      <Drawer
        title="Quickly Re-order"
        open={props.reOrderDrawerOpen}
        onClose={() => {
          props.setReOrderDrawerOpen(false);
        }}
      >
        <Box paddingBottom="5">
          <Stack space="2">
            {props.recentCarts.map((recentCart) => (
              <ReOrderItem
                key={recentCart.timestamp}
                locationRef={locationRef}
                cartRecord={recentCart}
                onReOrder={() => {
                  props.setReOrderDrawerOpen(false);
                }}
              />
            ))}
          </Stack>
        </Box>
        <Button
          primary
          onClick={() => {
            props.setReOrderDrawerOpen(false);
          }}
        >
          Cancel
        </Button>
      </Drawer>
    </>
  );
}

function CentreNavItem(props: {
  locationRef: string;
  cart: Cart;
  recentCarts: CartRecord[];
  reOrderLoading: boolean;
  setReOrderDrawerOpen: (open: boolean) => void;
}): React.ReactNode {
  const { locationRef } = props;

  const cartCount = props.cart.items.reduce(
    (total, item) => total + item.quantity,
    0,
  );

  if (cartCount > 0)
    return (
      <RaisedNavItem
        name="CART"
        href={`/${locationRef}/cart`}
        count={cartCount.toString()}
      >
        <CartIcon />
      </RaisedNavItem>
    );

  if (props.recentCarts.length > 0 && !props.reOrderLoading)
    return (
      <NavItem
        name="RE-ORDER"
        active={false}
        onClick={() => {
          props.setReOrderDrawerOpen(true);
        }}
      >
        <BoltIcon color="currentColor" />
      </NavItem>
    );

  return (
    <NavItem name="CART" href={`/${locationRef}/cart`} active={false}>
      <CartIcon />
    </NavItem>
  );
}

function NavItem(props: {
  name: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  children: React.ReactNode;
}): React.ReactNode {
  const className = [
    styles.navItemLink,
    props.active ? styles.active : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <div className={styles.navItem}>
      <div className={styles.iconContainer}>{props.children}</div>
      {props.active ? (
        <b className={styles.label}>{props.name}</b>
      ) : (
        <span className={styles.label}>{props.name}</span>
      )}
    </div>
  );

  if (props.href)
    return (
      <Link href={props.href} scroll={false} className={className}>
        {content}
      </Link>
    );

  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={!props.onClick}
      className={className}
    >
      {content}
    </button>
  );
}

function RaisedNavItem(props: {
  name: string;
  href: string;
  count: string;
  children?: React.ReactNode;
}): React.ReactNode {
  const [spinningBack, setSpinningBack] = useState(false);

  const flipperClassName = [
    styles.flipper,
    spinningBack ? styles.flipBack : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Link
      href={props.href}
      scroll={false}
      className={`${styles.navItemLink} ${styles.centreLink}`}
      onClick={() => {
        setSpinningBack(true);
      }}
    >
      <div className={styles.centreItem}>
        <div className={styles.circle}>
          <div
            className={flipperClassName}
            onAnimationEnd={() => {
              setSpinningBack(false);
            }}
          >
            <div className={`${styles.face} ${styles.faceJetman}`}>
              <Image
                alt=""
                src="/images/jetman_circle.png"
                width={JETMAN_WIDTH_PIXELS}
                height={JETMAN_HEIGHT_PIXELS}
                priority
              />
            </div>
            <div className={`${styles.face} ${styles.faceCart}`}>
              {props.children ? (
                <>
                  <div className={styles.iconContainer}>{props.children}</div>
                  <span>{props.count}</span>
                </>
              ) : (
                <span className={styles.circleCount}>{props.count}</span>
              )}
            </div>
          </div>
        </div>
        <span className={styles.label}>{props.name}</span>
      </div>
    </Link>
  );
}

function ReOrderItem(props: {
  cartRecord: CartRecord;
  locationRef: string;
  onReOrder: () => void;
}): React.ReactNode {
  const router = useRouter();
  const { reOrderCart } = useCart(props.locationRef);

  const recentCart = props.cartRecord.items.length
    ? mapCartRecordToCart(props.cartRecord)
    : null;

  const dispatchOption = props.cartRecord.fulfillmentType || "PICK_UP";

  const { deals } = useLocationDeals(props.locationRef, dispatchOption);

  const productIds = useMemo(
    () => recentCart?.items.map((item) => item.productId) ?? [],
    [recentCart],
  );

  // timeSlot is not taken into account for a re-order
  const { menu: cartMenu, isLoading: loadingMenu } = useCartMenu(
    dispatchOption,
    null,
    productIds,
  );

  const orderNotesMaxLength = useOrderNotesMaxLength(
    props.locationRef,
    dispatchOption,
  );
  const allowOrderNotes = useAllowOrderNotes(props.locationRef, dispatchOption);

  const { cartDetails } = useMemo(
    () => mapCartDetails(recentCart?.items ?? [], cartMenu, deals),
    [recentCart, cartMenu, deals],
  );

  const handlePressReorder = () => {
    if (!recentCart?.items.length) return;

    const notes = props.cartRecord.notes?.substring(0, orderNotesMaxLength);

    reOrderCart(
      props.locationRef,
      recentCart,
      dispatchOption,
      allowOrderNotes ? notes || "" : "",
    );

    props.onReOrder();
    router.push(`/${props.locationRef}/cart`, { scroll: false });
  };

  return (
    <OptionButton
      icon="bolt"
      loading={loadingMenu}
      title={cartDetails.map((cartItem) => cartItem.product?.title).join(", ")}
      subTitle={
        <Text>
          Ordered{" "}
          {formatRelative(new Date(props.cartRecord.timestamp), new Date())}
        </Text>
      }
      onPress={handlePressReorder}
    />
  );
}

function getActiveItem(page: Page | undefined): NavBarItem | undefined {
  switch (page) {
    case "menu":
    case "menu-item":
      return "MENU";
    case "loyalty":
      return "LOYALTY";
    case "promo-code":
      return "CODE";
    case "account":
    case "activity":
    case "transactions":
      return "ACCOUNT";
    default:
      return undefined;
  }
}
